<template>
  <Modal
    :value="modalPriceUpdateIsOpen"
    :onClose="cancel"
    title="Atualização de Preço de Venda"
    size="large"
  >
    <b-row class="form-group table-list-products" v-if="productCollection.length">
      <table class="table table-price-update">
        <thead class="fixed">
          <tr>
            <th class="col-4">Descrição</th>
            <th class="text-right col-2">Preço de Custo</th>
            <th class="text-right col-2">
              Lucro Bruto
              <template v-if="client.isDefaultProfitPercentEnabled">
                (Padrão {{ roundAndFormat(defaultProfitPercent, 2) }}%)
              </template>
            </th>
            <th class="text-right col-2">Preço Atual</th>
            <th class="text-right col-2">Novo Preço</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(item, key) in productCollection" :key="key">
            <td class="col-4">{{ item.description }}</td>
            <td class="text-right col-2">
              {{ roundAndFormat(valueConverted(item.cost || 0.0, item.unitOfMeasure), decimalConfiguration.price) }}
            </td>
            <td class="text-right col-2">
              <template v-if="parseFloat(item.product.profitPercent) >= 0">
                ({{ roundAndFormat(item.product.profitPercent, 2) }}%)
              </template>
              {{ roundAndFormat(getProductProfit(item), decimalConfiguration.price) }}
            </td>
            <td class="text-right col-2">
              {{ roundAndFormat(item.product.price || 0.0, decimalConfiguration.price) }}
            </td>
            <td class="text-right col-2">
              <InputMoney
                ref="unitPrice"
                id="unitPrice"
                inputClass="form-control input"
                v-model="item.product.newPrice"
                :prefix="false"
                labelBold
                :max="11"
              />
            </td>
          </tr>
        </tbody>
      </table>
    </b-row>

    <Confirm
      v-model="modalConfirm.open"
      :onOk="modalConfirm.onOk"
      :title="modalConfirm.title"
      :message="modalConfirm.message"
      :type="modalConfirm.type"
    />

    <b-row class="margin-button-save" align="center">
      <b-col>
        <Button type="cancel" @click="cancel">
          <Close class="mb-1" title="Cancelar" /> Cancelar
        </Button>
        <Button type="success" @click="updateAllToProfit">Atualizar pelo Lucro Bruto</Button>
        <Button type="success" @click="updateAllToDefaultPrice">Recalcular</Button>
        <Button type="success" @click="save">Salvar</Button>
      </b-col>
    </b-row>
  </Modal>
</template>

<script>
import Close from 'vue-material-design-icons/Close';
import { Modal, InputMoney, Button, Confirm } from '@/components';
import FormatMixin from '@/modules/Application/mixins/FormatMixin';
import NumbersMixin from '@/modules/Application/mixins/NumbersMixin';
import { mapActions, mapGetters, mapState } from 'vuex';
import {
  MULTIPLIER,
  DIVIDER,
} from '../../../../UnitOfMeasure/enums/UnitOfMeasureConverterOperatorEnum';

export default {
  name: 'ModalPriceUpdate',
  mixins: [FormatMixin, NumbersMixin],
  components: {
    Modal,
    InputMoney,
    Button,
    Close,
    Confirm,
  },
  props: {
    confirm: {
      type: Function,
    },
  },
  data: () => ({
    productCollection: [],
    modalConfirm: {
      title: '',
      message: '',
      type: 1,
      onOk: () => null,
      open: false,
    },
  }),
  async created() {
    const updatedCostsPurchase = await this.calculateProductsCost(this.purchase);
    if (updatedCostsPurchase) {
      const updatedProductsCost = updatedCostsPurchase.products.productOfPurchaseCollection.map(
        (it) => it.price,
      );

      const products = {
        productOfPurchaseCollection: this.items.map((it, index) => ({
          ...it,
          cost: updatedProductsCost[index],
        })),
      };

      await this.updatePurchaseData({
        ...this.purchase,
        products,
      });
    }

    const ids = this.items.map((item) => item.productId);

    const { data } = await this.$http.post('/inventory.get-product', {
      ids,
      active: true,
    });

    const products = Array.isArray(data) ? data : data.data;

    this.productCollection = this.items.map((item) => ({
      ...item,
      product: products.find((product) => product.id === item.productId) ?? {},
    }));

    this.updateAllToDefaultPrice();
  },
  computed: {
    ...mapState('componentsPurchase', ['modalPriceUpdateIsOpen']),
    ...mapState('client', ['client']),
    ...mapState('purchase', ['purchase', 'products']),
    ...mapGetters('purchase', ['decimalConfiguration']),
    defaultProfitPercent() {
      return (this.client.isDefaultProfitPercentEnabled && this.client.defaultProfitPercent) || 0.0;
    },
    items() {
      const { productOfPurchaseCollection } = this.purchase.products;
      return productOfPurchaseCollection;
    },
  },
  methods: {
    ...mapActions('componentsPurchase', ['toggleModalPriceUpdate']),
    ...mapActions('purchase', ['calculateProductsCost', 'updatePurchaseData']),
    cancel() {
      this.modalConfirm = {
        title: 'Cancelar Atualização de Preço',
        message: 'Tem certeza que deseja cancelar?',
        type: 1,
        onOk: () => {
          this.toggleModalPriceUpdate();
        },
        open: true,
      };
    },
    async save() {
      const { productOfPurchaseCollection } = this.purchase.products;
      const productsToUpdatePrice = productOfPurchaseCollection.map((product, index) => ({
        ...product,
        newPrice: this.toNumber(this.productCollection[index]?.product.newPrice),
      }));

      this.confirm(productsToUpdatePrice);
    },
    getProductProfit(item) {
      const { product } = item;
      const { price } = product;
      const cost = this.valueConverted(item.cost || 0.0, item.unitOfMeasure);
      const productProfit = product.profitPercent;

      if (parseFloat(product.profitPercent) === 0.0) return price;

      const profitPercent = productProfit || this.defaultProfitPercent;

      const profit = profitPercent > 0.0 ? (cost * profitPercent) / 100 : 0.0;

      return this.round(profit + cost, this.decimalConfiguration.price);
    },
    updateAllToProfit() {
      this.modalConfirm = {
        title: 'Atualização de Preço',
        message: 'Tem certeza que deseja Atualizar todos os produtos pelo Lucro Bruto?',
        type: 1,
        onOk: () => {
          this.productCollection = this.productCollection.map((item) => ({
            ...item,
            product: {
              ...item.product,
              newPrice: this.getProductProfit(item),
            },
          }));
        },
        open: true,
      };
    },
    updateAllToDefaultPrice() {
      this.productCollection = this.productCollection.map((item) => ({
        ...item,
        product: {
          ...item.product,
          newPrice: item.product.id ? item.product.price : 0.0,
        },
      }));
    },
    valueConverted(value, unitOfMeasure) {
      if (unitOfMeasure.converterOperator === DIVIDER) {
        return value * unitOfMeasure.converter;
      }

      if (unitOfMeasure.converterOperator === MULTIPLIER) {
        return value / unitOfMeasure.converter;
      }

      return value;
    },
  },
};
</script>

<style lang="scss">
#unitPrice {
  text-align: right;
}
table.table-price-update {
  thead {
    tr th {
      font-size: 13px;
    }

    &.fixed {
      position: sticky;
      top: 0px;
      background-color: #fff;
    }
  }

  tbody tr td {
    position: initial;
  }
}
</style>
